// src/pages/guides/CloudStorageExposureGuide.js
// Cloud Storage Exposure Response Guide - SEO Playbook Page

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function CloudStorageExposureGuide() {
  const startTrial = useTrialCta('guide_trial:cloud-storage-exposure');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Cloud Storage Exposure Guide",
    "description": "How to respond to exposed cloud storage (S3, Azure Blob, GCS). Immediate containment, data exposure assessment, and prevention controls.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2025-12-01T00:00:00-05:00",
    "dateModified": "2026-08-25T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/cloud-storage-exposure",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/cloud-storage-exposure"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "Cloud Storage Exposure" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="Cloud Storage Exposure Guide | DriftAlarm"
        description="How to respond to exposed cloud storage (S3, Azure Blob, GCS). Immediate containment, data exposure assessment, and why public buckets are actively hunted by attackers."
        path="/guides/cloud-storage-exposure"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/cloud-storage-exposure')].filter(Boolean)}
      />
      <PublicHeader activeLink="guides" />

      <main className="guide-content">
        <section className="guide-hero">
          <h1>Cloud Storage Exposure: When Your Data Is Public</h1>
          <GuideByline path="/guides/cloud-storage-exposure" />
          <GuideAnswer path="/guides/cloud-storage-exposure" />
          <p className="guide-hero-intro">
            Misconfigured cloud storage—S3 buckets, Azure Blobs, Google Cloud Storage—is one of the
            most common causes of major data breaches. Attackers continuously scan for exposed buckets,
            and your data can be exfiltrated within hours of misconfiguration.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>Why public cloud storage is actively hunted</li>
              <li>Immediate containment steps</li>
              <li>Data exposure assessment</li>
              <li>Notification and compliance obligations</li>
              <li>Prevention controls for each cloud provider</li>
            </ul>
          </div>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
            <Link to="/guides/attack-surface-monitoring" className="da-btn da-btn--secondary">
              Back to Attack Surface Monitoring Guide
            </Link>
          </div>
        </section>

        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#why-critical">Why This Is Critical</a></li>
            <li><a href="#attacker-tools">How Attackers Find Your Buckets</a></li>
            <li><a href="#priority-assessment">Priority Assessment</a></li>
            <li><a href="#immediate-response">Immediate Response</a></li>
            <li><a href="#exposure-assessment">Exposure Assessment</a></li>
            <li><a href="#prevention">Prevention by Provider</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        <section id="why-critical" className="guide-section">
          <h2>Why Cloud Storage Exposure Is Critical</h2>

          <div className="callout callout-critical">
            <div className="callout-title">P1 Until Validated, P0 If Sensitive Data Confirmed</div>
            <div className="callout-content">
              <p>
                <strong>Any public cloud storage with read or write access is an instant P1.</strong>
                If you confirm sensitive data (PII, credentials, proprietary data) is exposed, escalate
                to P0 and engage incident response.
              </p>
            </div>
          </div>

          <p>
            Cloud storage misconfigurations have caused some of the largest data breaches in recent years:
          </p>
          <ul>
            <li>Major financial institutions have exposed millions of customer records via misconfigured S3</li>
            <li>Prominent tech companies have leaked source code and internal data from exposed storage</li>
            <li>Enterprise cloud deployments have inadvertently exposed terabytes of customer data</li>
            <li>Countless smaller breaches go unreported every month</li>
          </ul>

          <div className="callout callout-warning">
            <div className="callout-title">Attackers Are Actively Scanning</div>
            <div className="callout-content">
              <p>
                <strong>Specialized tools scan for exposed cloud storage 24/7.</strong> Within hours of
                misconfiguring a bucket, it will be discovered, indexed, and potentially exfiltrated.
                Some researchers and criminals maintain databases of discovered public buckets.
              </p>
            </div>
          </div>
        </section>

        <section id="attacker-tools" className="guide-section">
          <h2>How Attackers Find Your Buckets</h2>

          <h3>Automated Discovery Methods</h3>
          <ul>
            <li><strong>Bucket name guessing:</strong> Trying common patterns (companyname-backup,
                companyname-data, companyname-prod)</li>
            <li><strong>DNS enumeration:</strong> Finding S3 buckets via CNAME records</li>
            <li><strong>Certificate Transparency:</strong> Discovering bucket names in SSL certificates</li>
            <li><strong>Google dorking:</strong> Searching for exposed bucket URLs indexed by search engines</li>
            <li><strong>Shodan/Censys:</strong> Internet-wide scanning for cloud storage signatures</li>
            <li><strong>GitHub/code scanning:</strong> Finding bucket names in source code repositories</li>
          </ul>

          <h3>Tools Used by Attackers</h3>
          <ul>
            <li>S3Scanner, AWSBucketDump, bucket-finder</li>
            <li>GCPBucketBrute, cloud_enum</li>
            <li>Specialized scripts targeting specific industries or naming conventions</li>
          </ul>

          <div className="example-box">
            <div className="example-title">Discovery to Exfiltration Timeline</div>
            <div className="example-content">
              <p>
                A representative timeline: Bucket misconfigured → automated scanners discover it within hours →
                attackers validate access and begin downloading shortly after → complete exfiltration of large
                datasets possible within the same day → data may appear on dark web forums within days.
              </p>
              <p>
                <strong>Speed matters. Containment must be immediate.</strong>
              </p>
            </div>
          </div>
        </section>

        <section id="priority-assessment" className="guide-section">
          <h2>Priority Assessment</h2>

          <h3>Access Level Determines Priority</h3>
          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Access Level</th>
                  <th>Risk</th>
                  <th>Priority</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Public read + sensitive data confirmed</td>
                  <td>Critical</td>
                  <td><span className="priority-badge p0">P0</span></td>
                </tr>
                <tr>
                  <td>Public write access (any content)</td>
                  <td>Critical</td>
                  <td><span className="priority-badge p0">P0</span></td>
                </tr>
                <tr>
                  <td>Public read (unvalidated content)</td>
                  <td>High</td>
                  <td><span className="priority-badge p1">P1</span></td>
                </tr>
                <tr>
                  <td>Public listing (can see filenames)</td>
                  <td>High</td>
                  <td><span className="priority-badge p1">P1</span></td>
                </tr>
                <tr>
                  <td>Authenticated user access too broad</td>
                  <td>Medium</td>
                  <td><span className="priority-badge p2">P2</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Content Type Escalation</h3>
          <p>If public access exists, the content determines whether to escalate to P0:</p>
          <ul>
            <li><strong>P0 content:</strong> PII (names, SSNs, addresses), credentials, API keys,
                source code, financial data, health records, legal documents</li>
            <li><strong>P1 content:</strong> Internal documents, configuration files, logs with
                potentially sensitive info, customer lists</li>
            <li><strong>P2 content:</strong> Public marketing materials, non-sensitive assets</li>
          </ul>

          <div className="callout callout-critical">
            <div className="callout-title">Write Access = Immediate P0</div>
            <div className="callout-content">
              <p>
                <strong>If public write access exists, this is P0 regardless of content.</strong>
                Attackers can inject malicious content, modify existing files, or use your storage
                for malware distribution. This creates legal and reputational liability.
              </p>
            </div>
          </div>
        </section>

        <section id="immediate-response" className="guide-section">
          <h2>Immediate Response (0-4 Hours)</h2>

          <h3>Step 1: Remove Public Access (First 15 Minutes)</h3>
          <p>
            Remove public access immediately. Don't wait to investigate contents first.
          </p>

          <h4>AWS S3</h4>
          <div className="scoring-formula">
            <div className="scoring-formula-title">AWS CLI Commands</div>
            <div className="scoring-formula-line"># Block all public access</div>
            <div className="scoring-formula-line"><code>aws s3api put-public-access-block \</code></div>
            <div className="scoring-formula-line"><code>  --bucket BUCKET-NAME \</code></div>
            <div className="scoring-formula-line"><code>  --public-access-block-configuration \</code></div>
            <div className="scoring-formula-line"><code>  BlockPublicAcls=true,IgnorePublicAcls=true,\</code></div>
            <div className="scoring-formula-line"><code>  BlockPublicPolicy=true,RestrictPublicBuckets=true</code></div>
          </div>

          <h4>Azure Blob Storage</h4>
          <ul>
            <li>Navigate to Storage Account → Configuration</li>
            <li>Set "Allow Blob public access" to Disabled (storage accounts created after November 2023 already default to disabled — confirm rather than assume)</li>
            <li>Review and remove any container-level public access</li>
          </ul>

          <h4>Google Cloud Storage</h4>
          <div className="scoring-formula">
            <div className="scoring-formula-title">gcloud Commands</div>
            <div className="scoring-formula-line"># Remove public access</div>
            <div className="scoring-formula-line"><code>gsutil iam ch -d allUsers gs://BUCKET-NAME</code></div>
            <div className="scoring-formula-line"><code>gsutil iam ch -d allAuthenticatedUsers gs://BUCKET-NAME</code></div>
          </div>

          <h3>Step 2: Verify Access Is Blocked</h3>
          <ul>
            <li>Test access from unauthenticated browser/curl</li>
            <li>Verify bucket no longer appears in public bucket scanners</li>
            <li>Confirm legitimate application access still works</li>
          </ul>

          <h3>Step 3: Preserve Access Logs</h3>
          <ul>
            <li>Enable access logging if not already enabled</li>
            <li>Export existing access logs for forensic analysis</li>
            <li>Identify time range of public exposure</li>
            <li>Note: AWS S3 server access logs may have 1-hour delay</li>
          </ul>

          <h3>Step 4: Notify Stakeholders</h3>
          <ul>
            <li>Inform security team and data owners</li>
            <li>Brief legal/compliance if sensitive data may be involved</li>
            <li>Prepare for potential breach notification requirements</li>
          </ul>
        </section>

        <section id="exposure-assessment" className="guide-section">
          <h2>Exposure Assessment</h2>

          <h3>Determine Exposure Window</h3>
          <ul>
            <li>When was the bucket created or when did public access start?</li>
            <li>Review IAM/configuration change history</li>
            <li>Check deployment logs and infrastructure-as-code history</li>
            <li>Correlate with any recent changes or deployments</li>
          </ul>

          <h3>Analyze Access Logs</h3>
          <p>Look for evidence of unauthorized access:</p>
          <ul>
            <li>Requests from unknown IP addresses</li>
            <li>Bulk download patterns (many objects accessed sequentially)</li>
            <li>Access from cloud provider IP ranges (other AWS accounts, etc.)</li>
            <li>Requests from known malicious IPs or security researcher ranges</li>
          </ul>

          <h3>Inventory Exposed Data</h3>
          <ul>
            <li>List all objects that were publicly accessible</li>
            <li>Classify data by sensitivity level</li>
            <li>Identify any regulated data (HIPAA, PCI, GDPR)</li>
            <li>Document for potential breach notification</li>
          </ul>

          <div className="callout callout-warning">
            <div className="callout-title">Assume Data Was Accessed</div>
            <div className="callout-content">
              <p>
                <strong>If sensitive data was publicly accessible for more than a few hours, assume
                it was exfiltrated.</strong> Attackers automate bucket discovery and download.
                The absence of access logs does not mean data wasn't taken—logging may not have
                been enabled, or logs may be incomplete.
              </p>
            </div>
          </div>

          <h3>Breach Notification Considerations</h3>
          <p>If sensitive data was exposed, consider notification obligations:</p>
          <ul>
            <li><strong>GDPR:</strong> 72-hour notification to supervisory authority</li>
            <li><strong>HIPAA:</strong> notify affected individuals without unreasonable delay and no later than 60 days; breaches affecting 500+ individuals also require contemporaneous notice to HHS and the media, while smaller breaches are reported to HHS annually</li>
            <li><strong>State laws:</strong> Varying requirements (California, New York, etc.)</li>
            <li><strong>Contractual:</strong> Customer and partner notification requirements</li>
            <li>Consult legal counsel before making notification decisions</li>
          </ul>
        </section>

        <section id="prevention" className="guide-section">
          <h2>Prevention Controls by Provider</h2>

          <h3>AWS S3</h3>
          <ul>
            <li><strong>Account-level block:</strong> Enable S3 Block Public Access at the account level</li>
            <li><strong>Bucket policies:</strong> Review all bucket policies for Principal: "*"</li>
            <li><strong>ACLs:</strong> Disable ACLs or ensure no public grants</li>
            <li><strong>AWS Config:</strong> Create rules to detect public bucket configurations</li>
            <li><strong>CloudTrail:</strong> Monitor for PutBucketPolicy and PutBucketAcl events</li>
            <li><strong>SCPs:</strong> Use Service Control Policies to prevent public bucket creation</li>
          </ul>

          <h3>Azure Blob Storage</h3>
          <ul>
            <li><strong>Storage account level:</strong> Disable "Allow Blob public access" by default</li>
            <li><strong>Azure Policy:</strong> Enforce private-only storage accounts</li>
            <li><strong>Private endpoints:</strong> Use private endpoints for sensitive storage</li>
            <li><strong>Defender for Storage:</strong> Enable threat detection</li>
            <li><strong>Activity logs:</strong> Monitor for access policy changes</li>
          </ul>

          <h3>Google Cloud Storage</h3>
          <ul>
            <li><strong>Organization policy:</strong> Set constraints/storage.publicAccessPrevention</li>
            <li><strong>Uniform bucket-level access:</strong> Simplify and enforce consistent permissions</li>
            <li><strong>VPC Service Controls:</strong> Create perimeters around sensitive data</li>
            <li><strong>Data Loss Prevention:</strong> Scan for sensitive content</li>
            <li><strong>Audit logs:</strong> Monitor for IAM policy changes</li>
          </ul>

          <div className="callout callout-info">
            <div className="callout-title">Defense in Depth</div>
            <div className="callout-content">
              <p>
                <strong>Layer your controls:</strong> Account-level blocks prevent accidental exposure,
                policy enforcement catches configuration drift, monitoring detects any exposure that
                slips through, and regular scanning validates your posture.
              </p>
            </div>
          </div>

          <h3>Continuous Monitoring</h3>
          <ul>
            <li>External attack surface monitoring to detect exposed storage</li>
            <li>Cloud Security Posture Management (CSPM) tools</li>
            <li>Regular configuration audits</li>
            <li>Automated remediation for policy violations</li>
          </ul>
        </section>

        {/* PRD-193 WP4: this guide previously had NO link to /upgrade or
            /how-it-works -- its only exit was a trial button that went to the sign-IN
            form. Mid-article conversion path, matching the block the other guides use. */}
        <section className="guide-section">
          <div className="guide-cta-section">
            <h3>How DriftAlarm Helps</h3>
            <p>Bucket permissions drift quietly — a policy edit, a new deployment, a copied configuration. Continuous external scanning re-checks your public storage every cycle, so an object store that becomes readable surfaces as a drift alert instead of waiting for the next audit to catch it.</p>
            <div className="guide-cta-buttons">
              <button onClick={startTrial} className="da-btn da-btn--primary">Start Free 30-Day Trial</button>
              <Link to="/how-it-works" className="da-btn da-btn--secondary">See How It Works</Link>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            Cloud storage exposure is often discovered alongside other attack surface issues. Review these related guides:
          </p>
          <ul>
            <li>
              <Link to="/guides/exposed-admin-panels">Exposed Admin Panels Guide</Link> — Cloud provider consoles and database management interfaces require similar immediate containment when exposed.
            </li>
            <li>
              <Link to="/guides/exposed-rdp">Exposed RDP Guide</Link> — Virtual machines with exposed RDP often accompany misconfigured cloud storage in hasty deployments.
            </li>
            <li>
              <Link to="/guides/exposed-ssh">Exposed SSH Guide</Link> — Cloud workloads commonly expose both SSH and storage—review both when investigating cloud misconfigurations.
            </li>
            <li>
              <Link to="/guides/attack-surface-monitoring">Attack Surface Monitoring Guide</Link> — Learn how to continuously monitor your cloud footprint to detect storage and infrastructure exposures before attackers do.
            </li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>Summary: Cloud Storage Response Checklist</h2>
          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Timeframe</th>
                  <th>Action</th>
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>0-15 min</td>
                  <td>Remove public access from bucket/container</td>
                  <td>Cloud/Security</td>
                </tr>
                <tr>
                  <td>15-30 min</td>
                  <td>Verify block, preserve access logs</td>
                  <td>Security</td>
                </tr>
                <tr>
                  <td>30-60 min</td>
                  <td>Notify stakeholders, brief legal if sensitive data</td>
                  <td>Security/Legal</td>
                </tr>
                <tr>
                  <td>1-4 hours</td>
                  <td>Inventory exposed data, analyze access logs</td>
                  <td>Security</td>
                </tr>
                <tr>
                  <td>1-3 days</td>
                  <td>Complete exposure assessment, determine notification needs</td>
                  <td>Security/Legal</td>
                </tr>
                <tr>
                  <td>1-2 weeks</td>
                  <td>Implement prevention controls, update policies</td>
                  <td>Cloud/Security</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
          </div>
        </section>
        <GuideFAQ path="/guides/cloud-storage-exposure" />
      </main>

      <PublicFooter />
    </div>
  );
}

export default CloudStorageExposureGuide;
